"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { SignInButton, SignedIn, SignedOut, useUser, CLERK_ENABLED } from "@/lib/auth-shim/client";
import { CustomUserButton } from "@/components/CustomUserButton";
import { InviteFriendsButton } from "@/components/InviteFriendsButton";
import { BugReportButton } from "@/components/BugReportDialog";
import { canUseChallenges } from "@/lib/feature-access";
import { applyNavGates, type NavGate } from "@/lib/nav-gates";
import { fetchCurrentUser, fetchMyClasses } from "@/lib/client-session-cache";
import {
    applySideNavCollapsed,
    readSideNavCollapsed,
} from "@/lib/sidenav-collapse";

type NavEntry = {
    href: string;
    label: string;
    icon: NavIconName;
    exact?: boolean;
    gate?: NavGate;
    /** Pages that server-redirect guests to "/". When signed out we
     *  intercept the click and pop the Clerk sign-in modal instead of
     *  letting the user bounce off the redirect. */
    requiresAuth?: boolean;
};

const NAV_ITEMS: NavEntry[] = [
    { href: "/", label: "Learn", icon: "learn", exact: true },
    { href: "/dictionary", label: "Dictionary", icon: "dictionary" },
    { href: "/challenges", label: "Challenges", icon: "challenges" },
    { href: "/quests", label: "Quests", icon: "quests" },
    { href: "/memory", label: "Memory", icon: "memory" },
    { href: "/progress", label: "Progress", icon: "progress", requiresAuth: true },
    { href: "/improve", label: "Improve", icon: "improve", requiresAuth: true },
    { href: "/calibrate", label: "Calibrate", icon: "calibrate" },
    {
        href: "/classes",
        label: "Classes",
        icon: "classes",
        requiresAuth: true,
        gate: "classes",
    },
    {
        href: "/dashboard",
        label: "Dashboard",
        icon: "dashboard",
        requiresAuth: true,
        gate: "dashboard",
    },
    {
        href: "/school",
        label: "School",
        icon: "school",
        requiresAuth: true,
        gate: "school",
    },
    { href: "/plans", label: "Plans", icon: "plans" },
    { href: "/changelog", label: "Changelog", icon: "logs" },
];

export function SideNav() {
    const pathname = usePathname() ?? "/";
    const { isSignedIn, isLoaded } = useUser();
    /** Drawer state — only meaningful under the 1024px breakpoint, where the
     *  rail is off-canvas. Above it the CSS ignores the class entirely. */
    const [menuOpen, setMenuOpen] = useState(false);
    /** Desktop collapse. The width itself is driven by `data-sidenav-collapsed`
     *  on <html>, stamped before paint by the layout bootstrap, so this state
     *  exists only to label the toggle. It starts false and syncs on mount:
     *  reading localStorage during the hydration render would disagree with
     *  the server HTML for everyone who has ever collapsed the rail. */
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
        setCollapsed(readSideNavCollapsed());
    }, []);

    const toggleCollapsed = () => {
        setCollapsed((prev) => {
            const next = !prev;
            applySideNavCollapsed(next);
            return next;
        });
    };

    useEffect(() => {
        if (!isLoaded) return;
        if (!isSignedIn) {
            applyNavGates({ classes: false, dashboard: false, school: false, student: false });
            return;
        }
        let cancelled = false;
        Promise.all([fetchCurrentUser(), fetchMyClasses()])
            .then(([user, rows]) => {
                if (cancelled) return;
                const next: Partial<Record<NavGate, boolean>> = {};
                if (user) {
                    next.dashboard = Boolean(user.is_supervision_account);
                    next.school = user.school_role === "supervisor";
                    next.student = user.school_role === "student";
                }
                if (rows) {
                    next.classes = rows.length > 0;
                }
                applyNavGates(next);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [isLoaded, isSignedIn]);

    // A tap on a nav link is a same-page route change, not an unmount, so the
    // drawer has to close itself when the path lands.
    useEffect(() => {
        setMenuOpen(false);
    }, [pathname]);

    // Escape closes the drawer, and the page behind it must not scroll while
    // it's open — otherwise the backdrop drags the content underneath on iOS.
    useEffect(() => {
        if (!menuOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setMenuOpen(false);
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKey);
        };
    }, [menuOpen]);

    const visibleNav = NAV_ITEMS.filter((entry) => {
        if (entry.href === "/challenges") return canUseChallenges(null);
        return true;
    });

    const isActive = (entry: NavEntry): boolean => {
        if (entry.exact) return pathname === entry.href;
        return pathname === entry.href || pathname.startsWith(entry.href + "/");
    };

    const joinActive = pathname === "/join" || pathname.startsWith("/join/");

    return (
        <>
            {/* Phone/tablet affordance. Fixed bottom-left so it never collides
                with the per-page "Back to Signpost" headers at the top of the
                content, and mirrors the theme toggle in the opposite corner.
                Hidden by CSS above 1024px, where the rail is always visible. */}
            <button
                type="button"
                className="app-sidenav-toggle"
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                aria-expanded={menuOpen}
                aria-controls="app-sidenav"
                onClick={() => setMenuOpen((v) => !v)}
            >
                {menuOpen ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" width={20} height={20}>
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" width={20} height={20}>
                        <path d="M3 6h18" />
                        <path d="M3 12h18" />
                        <path d="M3 18h18" />
                    </svg>
                )}
            </button>

            <div
                className={
                    "app-sidenav-backdrop" +
                    (menuOpen ? " app-sidenav-backdrop--open" : "")
                }
                onClick={() => setMenuOpen(false)}
                aria-hidden="true"
            />

            <aside
                id="app-sidenav"
                className={"app-sidenav" + (menuOpen ? " app-sidenav--open" : "")}
                aria-label="Primary navigation"
            >
                <div className="app-sidenav__head">
                    <a href="/" className="app-sidenav__logo">
                        {/* Both marks ship; which one shows is decided by the
                            collapsed attribute on <html>, which is already set
                            before first paint. Swapping them in JS instead
                            would flash the wordmark on every collapsed load. */}
                        <img
                            className="app-sidenav__logo-full"
                            src="/text-logo.png"
                            alt="Signpost"
                        />
                        <img
                            className="app-sidenav__logo-mark"
                            src="/favicon-192.png"
                            alt="Signpost"
                        />
                    </a>

                    <button
                        type="button"
                        className="app-sidenav__collapse"
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                        aria-expanded={!collapsed}
                        aria-controls="app-sidenav"
                        onClick={toggleCollapsed}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            width={18}
                            height={18}
                        >
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <path d="M9 3v18" />
                            <path
                                className="app-sidenav__collapse-chevron"
                                d="m16 15-3-3 3-3"
                            />
                        </svg>
                    </button>
                </div>

                <nav className="app-sidenav__nav">
                    {visibleNav.map((entry) => {
                        const active = isActive(entry);
                        const className =
                            "app-sidenav__item" +
                            (active ? " app-sidenav__item--active" : "") +
                            (entry.gate
                                ? ` app-sidenav__item--gated app-sidenav__item--gate-${entry.gate}`
                                : "");
                        const inner = (
                            <>
                                <NavIcon name={entry.icon} />
                                <span>{entry.label}</span>
                            </>
                        );

                        // Auth-required page + confirmed signed-out → intercept the
                        // click with the Clerk sign-in modal so the user gets a
                        // prompt instead of silently bouncing off the server
                        // redirect to "/". Gate on isLoaded so a signed-in user is
                        // never shown the modal during the auth-resolution window,
                        // and on CLERK_ENABLED so no-Clerk dev keeps plain links.
                        const gated =
                            Boolean(entry.requiresAuth) &&
                            CLERK_ENABLED &&
                            isLoaded &&
                            !isSignedIn;

                        if (gated) {
                            return (
                                <SignInButton
                                    key={entry.href}
                                    mode="modal"
                                    forceRedirectUrl={entry.href}
                                >
                                    <button
                                        type="button"
                                        className={className}
                                        data-nav-label={entry.label}
                                    >
                                        {inner}
                                    </button>
                                </SignInButton>
                            );
                        }

                        return (
                            <a
                                key={entry.href}
                                href={entry.href}
                                className={className}
                                data-nav-label={entry.label}
                                aria-current={active ? "page" : undefined}
                            >
                                {inner}
                            </a>
                        );
                    })}

                    <a
                        href="/join"
                        className={
                            "app-sidenav__join" +
                            (joinActive ? " app-sidenav__join--active" : "")
                        }
                        data-nav-label="Join a Class"
                        aria-current={joinActive ? "page" : undefined}
                    >
                        <NavIcon name="join" />
                        <span>Join a Class</span>
                    </a>

                    <InviteFriendsButton />
                </nav>

                <div className="app-sidenav__footer">
                    <BugReportButton variant="sidenav" />
                    <SignedOut>
                        <SignInButton mode="modal" forceRedirectUrl="/">
                            <button
                                className="app-sidenav__signin"
                                type="button"
                                data-nav-label="Sign In"
                            >
                                <NavIcon name="user" />
                                <span>Sign In</span>
                            </button>
                        </SignInButton>
                    </SignedOut>
                    <SignedIn>
                        <div className="app-sidenav__user-chip" data-nav-label="Account">
                            <CustomUserButton />
                            <span>Account</span>
                        </div>
                    </SignedIn>
                </div>
            </aside>
        </>
    );
}

type NavIconName =
    | "learn"
    | "dictionary"
    | "challenges"
    | "quests"
    | "memory"
    | "progress"
    | "improve"
    | "calibrate"
    | "plans"
    | "logs"
    | "join"
    | "classes"
    | "dashboard"
    | "school"
    | "user";

function NavIcon({ name }: { name: NavIconName }) {
    const common = {
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 2,
        strokeLinecap: "round" as const,
        strokeLinejoin: "round" as const,
        width: 18,
        height: 18,
    };
    switch (name) {
        case "learn":
            return (
                <svg {...common}>
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
            );
        case "dictionary":
            return (
                <svg {...common}>
                    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20" />
                    <path d="m8 13 2.5-5 2.5 5" />
                    <path d="M8.5 12h4" />
                </svg>
            );
        case "challenges":
            return (
                <svg {...common}>
                    <circle cx="12" cy="13" r="8" />
                    <path d="M12 9v4l2 2" />
                    <path d="M9 2h6" />
                    <path d="M12 2v3" />
                </svg>
            );
        case "quests":
            return (
                <svg {...common}>
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5C7 4 7 7 7 7" />
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5C17 4 17 7 17 7" />
                    <path d="M4 22h16" />
                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22" />
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22" />
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                </svg>
            );
        case "memory":
            // Lucide "brain"
            return (
                <svg {...common}>
                    <path d="M12 18V5" />
                    <path d="M15 13a4.17 4.17 0 0 1-3-4 4.17 4.17 0 0 1-3 4" />
                    <path d="M17.598 6.5A3 3 0 1 0 12 5a3 3 0 1 0-5.598 1.5" />
                    <path d="M17.997 5.125a4 4 0 0 1 2.526 5.77" />
                    <path d="M18 18a4 4 0 0 0 2-7.464" />
                    <path d="M19.967 17.483A4 4 0 1 1 12 18a4 4 0 1 1-7.967-.517" />
                    <path d="M6 18a4 4 0 0 1-2-7.464" />
                    <path d="M6.003 5.125a4 4 0 0 0-2.526 5.77" />
                </svg>
            );
        case "progress":
            return (
                <svg {...common}>
                    <path d="M3 3v18h18" />
                    <path d="M7 14l3-3 4 4 5-6" />
                </svg>
            );
        case "improve":
            return (
                <svg {...common}>
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="6" />
                    <circle cx="12" cy="12" r="2" />
                </svg>
            );
        case "calibrate":
            return (
                <svg {...common}>
                    <path d="M4 21v-7" />
                    <path d="M4 10V3" />
                    <path d="M12 21v-9" />
                    <path d="M12 8V3" />
                    <path d="M20 21v-5" />
                    <path d="M20 12V3" />
                    <path d="M1 14h6" />
                    <path d="M9 8h6" />
                    <path d="M17 16h6" />
                </svg>
            );
        case "plans":
            return (
                <svg {...common}>
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                    <line x1="7" y1="15" x2="9" y2="15" />
                </svg>
            );
        case "logs":
            return (
                <svg {...common}>
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                </svg>
            );
        case "join":
            return (
                <svg {...common}>
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    <path d="m10 17 5-5-5-5" />
                    <path d="M15 12H3" />
                </svg>
            );
        case "classes":
            return (
                <svg {...common}>
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
            );
        case "dashboard":
            return (
                <svg {...common}>
                    <rect x="3" y="3" width="7" height="9" rx="1" />
                    <rect x="14" y="3" width="7" height="5" rx="1" />
                    <rect x="14" y="12" width="7" height="9" rx="1" />
                    <rect x="3" y="16" width="7" height="5" rx="1" />
                </svg>
            );
        case "school":
            return (
                <svg {...common}>
                    <path d="M14 21v-3a2 2 0 0 0-4 0v3" />
                    <path d="M18 5v16" />
                    <path d="m4 6 7.106-3.79a2 2 0 0 1 1.788 0L20 6" />
                    <path d="m6 11-3.52 2.147a1 1 0 0 0-.48.854V19a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a1 1 0 0 0-.48-.853L18 11" />
                    <path d="M6 5v16" />
                    <circle cx="12" cy="9" r="2" />
                </svg>
            );
        case "user":
            return (
                <svg {...common}>
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                </svg>
            );
    }
}
